import { useCallback, useEffect, useState } from 'react'
import { get, patch } from '../lib/api'

/**
 * Internal tickets — where a request goes when the app could not do it.
 *
 * Most rows here are written by Voice of God escalating, so the list doubles as
 * the honest backlog of what the command line still cannot handle. The verbatim
 * instruction is kept beside each one: a paraphrase of a failed command loses
 * the thing that made it fail.
 */

type Ticket = {
  id: string; title: string; body: string | null; source: string; status: string
  priority: string; subject_ref: string | null; raw_command: string | null
  created_at: number; resolution: string | null
}

const NEXT: Record<string, string> = { open: 'in_progress', in_progress: 'done', blocked: 'open', done: 'open', wont_do: 'open' }
const LABEL: Record<string, string> = { open: 'Open', in_progress: 'In progress', blocked: 'Blocked', done: 'Done', wont_do: "Won't do" }

export default function Tickets() {
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const r = await get<{ tickets: Ticket[] }>('/api/tickets')
      setTickets(r.tickets)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load tickets.')
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function advance(t: Ticket) {
    setBusy(t.id)
    try {
      await patch(`/api/tickets/${t.id}`, { status: NEXT[t.status] ?? 'open' })
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update it.')
    } finally {
      setBusy(null)
    }
  }

  if (error) return <p className="error" role="alert">{error}</p>
  if (!tickets.length) {
    return <p className="muted">No tickets. Voice of God opens one whenever it cannot carry out an instruction.</p>
  }

  return (
    <table className="cc-table">
      <thead><tr><th>Ticket</th><th>Source</th><th>Raised</th><th>Status</th><th /></tr></thead>
      <tbody>
        {tickets.map((t) => (
          <tr key={t.id} className={t.status === 'done' || t.status === 'wont_do' ? '' : 'overdue'}>
            <td>
              <strong>{t.title}</strong>
              {t.raw_command && <code className="cc-cmd">{t.raw_command}</code>}
              {t.body && <span className="cc-sub">{t.body}</span>}
            </td>
            <td className="cc-sub">{t.source === 'voice-of-god' ? 'Voice of God' : 'you'}</td>
            <td className="cc-sub">{new Date(t.created_at).toLocaleDateString()}</td>
            <td><span className={`cc-badge ${t.status === 'done' ? 'ok' : 'bad'}`}>{LABEL[t.status] ?? t.status}</span></td>
            <td>
              <button type="button" className="link" disabled={busy === t.id} onClick={() => void advance(t)}>
                → {LABEL[NEXT[t.status] ?? 'open']}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
